// /src/lib/firebase/firestore.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "./config";
// Import types
import { Project, Artwork, HomePageData, Photo } from "../../types"; // types/index.ts

// Define project collection
const projectsCollection = collection(db, "projects"); // Collection reference for projects
// This collection will store all project documents

// === PROJECTS ===

export const listenForProjects = (callback: (projects: Project[]) => void) => {
  return onSnapshot(projectsCollection, (snapshot) => {
    const projects = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Project[];

    callback(projects);
  });
};

export const getProjects = async (): Promise<Project[]> => {
  const snapshot = await getDocs(projectsCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Project[];
};

export const addProject = async (project: Omit<Project, "id">) => {
  await addDoc(projectsCollection, project);
};

export const updateProject = async (
  id: string,
  updatedData: Partial<Project>
) => {
  const projectDoc = doc(db, "projects", id);
  await updateDoc(projectDoc, updatedData);
};

export const deleteProject = async (id: string) => {
  const projectDoc = doc(db, "projects", id);
  await deleteDoc(projectDoc);
};

export const getProjectById = async (id: string): Promise<Project | null> => {
  const projectDoc = doc(db, "projects", id);
  const snapshot = await getDoc(projectDoc);

  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Project;
};

// === ARTWORK ===

const artworksCollection = collection(db, "artworks");

export const getArtworks = async (): Promise<Artwork[]> => {
  const snapshot = await getDocs(artworksCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Artwork[];
};

export const addArtwork = async (artwork: Omit<Artwork, "id">) => {
  await addDoc(artworksCollection, artwork);
};

export const getArtworkById = async (id: string): Promise<Artwork | null> => {
  const artworkDoc = doc(db, "artworks", id);
  const snapshot = await getDoc(artworkDoc);

  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Artwork;
};

export const updateArtwork = async (
  id: string,
  updatedData: Partial<Artwork>
) => {
  const artworkDoc = doc(db, "artworks", id);
  await updateDoc(artworkDoc, updatedData);
};

export const deleteArtwork = async (id: string) => {
  const artworkDoc = doc(db, "artworks", id);
  await deleteDoc(artworkDoc);
};

// photo gallery specific functions
const photosCollection = collection(db, "photos");
export const listenForPhotos = (callback: (photos: Photo[]) => void) => {
  return onSnapshot(photosCollection, (snapshot) => {
    const photos = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Photo[];

    callback(photos);
  });
};

// Get all photos
export const getPhotos = async (): Promise<Photo[]> => {
  const snapshot = await getDocs(photosCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Photo[];
};

// Get photo by ID
export const getPhotoById = async (id: string): Promise<Photo | null> => {
  const photoDoc = doc(db, "photos", id);
  const snapshot = await getDoc(photoDoc);
  if (!snapshot.exists()) return null;

  return { id: snapshot.id, ...snapshot.data() } as Photo;
};

// Add a new photo
export const addPhoto = async (photo: Omit<Photo, "id">) => {
  await addDoc(photosCollection, {
    ...photo,
    createdAt: Timestamp.now(),
  });
};

// Update photo
export const updatePhoto = async (id: string, updatedData: Partial<Photo>) => {
  const photoDoc = doc(db, "photos", id);
  await updateDoc(photoDoc, updatedData);
};

// Delete photo
export const deletePhoto = async (id: string) => {
  const photoDoc = doc(db, "photos", id);
  await deleteDoc(photoDoc);
};

// === HOMEPAGE DATA ===

export const getHomePageData = async (): Promise<HomePageData | null> => {
  const docRef = doc(db, "settings", "homepage");
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) return null;

  return snapshot.data() as HomePageData;
};

export const updateHomePageData = async (data: HomePageData) => {
  const docRef = doc(db, "settings", "homepage");
  await setDoc(docRef, data);
};

// === FAQ ===
const faqsCollection = collection(db, "faqs");

export const getFaqs = async (): Promise<
  { id: string; question: string; answer: string }[]
> => {
  const snapshot = await getDocs(faqsCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as { id: string; question: string; answer: string }[];
};

export const addFaq = async (faq: { question: string; answer: string }) => {
  await addDoc(faqsCollection, faq);
};

export const updateFaq = async (
  id: string,
  updated: { question: string; answer: string }
) => {
  const faqDoc = doc(db, "faqs", id);
  await updateDoc(faqDoc, updated);
};

export const deleteFaq = async (id: string) => {
  const faqDoc = doc(db, "faqs", id);
  await deleteDoc(faqDoc);
};
// This file provides Firestore interaction functions for projects, artworks, homepage data, and FAQs.

// contact-us form submission
export const addContactUs = async (contact: {
  name: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  jobTitle: string;
  jobDetails: string;
}) => {
  await addDoc(collection(db, "contact-us"), {
    ...contact,
    createdAt: Timestamp.now(),
  });
};
// === CONTACT US ===
const contactUsCollection = collection(db, "contact-us");

export const getContactUs = async (): Promise<
  {
    id: string;
    name: string;
    email: string;
    phone: string;
    company: string;
    country: string;
    jobTitle: string;
    jobDetails: string;
    createdAt: any;
  }[]
> => {
  const snapshot = await getDocs(contactUsCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as {
    id: string;
    name: string;
    email: string;
    phone: string;
    company: string;
    country: string;
    jobTitle: string;
    jobDetails: string;
    createdAt: any;
  }[];
};
// ✅ Delete contact by ID
export const deleteContact = async (id: string) => {
  const docRef = doc(db, "contact-us", id);
  await deleteDoc(docRef);
};

// Feedback form submission
const feedbackCollection = collection(db, "feedback");

export const addFeedback = async (feedback: {
  rating: number;
  review: string;
}) => {
  await addDoc(feedbackCollection, {
    ...feedback,
    createdAt: Timestamp.now(),
  });
};

export const getFeedbacks = async (): Promise<
  { id: string; rating: number; review: string; createdAt: any }[]
> => {
  const snapshot = await getDocs(feedbackCollection);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as { id: string; rating: number; review: string; createdAt: any }[];
};
export const deleteFeedback = async (id: string) => {
  const docRef = doc(db, "feedback", id);
  await deleteDoc(docRef);
};
